import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtUSDStrict, timeAgo } from "@/lib/format";
import { ChevronDown, ChevronRight, X, Radio, CheckCircle2, Ban, Clock } from "lucide-react";

const num = (n, d = 6) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: d });

const STATUS = {
  pending: { label: "Working", cls: "bg-amber-500/12 text-amber-700 dark:text-amber-400", icon: Clock },
  partial: { label: "Partially filled", cls: "bg-sky-500/12 text-sky-700 dark:text-sky-400", icon: Radio },
  filled: { label: "Filled", cls: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-400", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", cls: "bg-muted text-muted-foreground", icon: Ban },
};

export const StatusPill = ({ status }) => {
  const s = STATUS[status] || STATUS.filled;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.08em] ${s.cls}`}
      data-testid={`order-status-${status}`}>
      <s.icon className="h-3 w-3" />{s.label}
    </span>
  );
};

const EventStream = ({ events }) => (
  <ol className="relative ms-2 border-s ps-4 space-y-3 py-1" data-testid="order-event-stream">
    {events.map((e) => (
      <li key={e.id} className="text-xs">
        <span className="absolute -start-[5px] mt-1.5 h-2 w-2 rounded-full bg-[color:var(--tharwy-lime)]" />
        <div className="font-semibold tracking-wide">{e.event_type}</div>
        <div className="text-muted-foreground">
          {Object.entries(e.payload)
            .filter(([k]) => !["order_id", "symbol"].includes(k))
            .map(([k, v]) => `${k.replace(/_/g, " ")} ${typeof v === "number" ? num(v) : String(v)}`)
            .join(" · ") || "—"}
        </div>
        <div className="text-[10px] text-muted-foreground/70 numeric">{timeAgo(e.timestamp)}</div>
      </li>
    ))}
    {!events.length && <li className="text-xs text-muted-foreground">No broker events recorded.</li>}
  </ol>
);

export const OrderRow = ({ order, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState(false);

  const expand = async () => {
    setOpen((v) => !v);
    if (!events) {
      try {
        const { data } = await api.get(`/orders/${order.id}`);
        setEvents(data.events);
      } catch { setEvents([]); }
    }
  };

  const cancel = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/cancel`);
      toast.success("Order cancelled", { description: data.message });
      onChanged && onChanged();
    } catch (err) {
      toast.error("Couldn't cancel", { description: err?.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const buy = order.side === "buy";
  return (
    <div className="border-b last:border-0" data-testid={`order-row-${order.id}`}>
      <div role="button" tabIndex={0} onClick={expand}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); expand(); } }}
        className="w-full text-start px-3.5 py-3 hover:bg-secondary/60 transition-colors cursor-pointer"
        data-testid={`order-row-toggle-${order.symbol}`}>
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{order.symbol}</span>
              <Badge variant={buy ? "default" : "destructive"}
                className={buy ? "bg-emerald-600 hover:bg-emerald-600" : ""}>
                {order.side.toUpperCase()}
              </Badge>
              <StatusPill status={order.status} />
              {order.confirmed_with === "face_id" && (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Face ID
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1 numeric">
              {num(order.qty)} {order.order_type === "trailing_stop"
                ? `· trailing ${order.trail_pct}% · stop ${num(order.stop_price, 4)}`
                : order.order_type === "stop" ? `· stop ${num(order.stop_price, 4)}`
                : order.order_type === "limit" ? `@ limit ${num(order.limit_price, 4)}` : "at market"}
              {" · "}{order.currency}
              {order.trigger_price && order.last_price ? ` · last ${num(order.last_price, 4)}` : ""}
              {order.away_pct !== null && order.away_pct !== undefined && order.status === "pending"
                ? ` · ${order.away_pct > 0 ? "+" : ""}${order.away_pct}% away` : ""}
            </div>
          </div>
          <div className="text-end shrink-0">
            <div className="numeric text-sm font-semibold">
              {fmtUSDStrict(order.cost_usd || order.notional_usd)}
            </div>
            <div className="text-[11px] text-muted-foreground numeric">{timeAgo(order.timestamp)}</div>
          </div>
          {order.cancellable && (
            <Button size="sm" variant="outline" onClick={cancel} disabled={busy}
              data-testid={`order-cancel-${order.symbol}`}>
              <X className="h-3.5 w-3.5 me-1" />Cancel
            </Button>
          )}
        </div>

        {(order.status === "partial" || order.status === "pending") && (
          <div className="mt-2.5 ms-7">
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div className="h-full rounded-full bg-[color:var(--tharwy-lime)] transition-[width] duration-500"
                style={{ width: `${order.filled_pct}%` }} />
            </div>
            <div className="text-[11px] text-muted-foreground mt-1 numeric">
              {num(order.filled_qty)} of {num(order.qty)} filled ({order.filled_pct}%)
              {order.hold_usd > 0 ? ` · ${fmtUSDStrict(order.hold_usd)} held` : ""}
            </div>
          </div>
        )}
      </div>

      {open && (
        <div className="px-3.5 pb-4 ps-10 grid gap-4 sm:grid-cols-2" data-testid={`order-detail-${order.symbol}`}>
          <div className="text-xs space-y-1.5">
            {[...(order.order_type === "trailing_stop"
                ? [["Trails", `${order.trail_pct}% below the high`],
                   ["High since armed", num(order.hwm, 4)]] : []),
              ["Venue", order.venue.replace(/_/g, " ")],
              ["Broker status", order.broker_status || "—"],
              ["Average fill", order.avg_fill_price ? num(order.avg_fill_price, 4) : "—"],
              ["Fee", fmtUSDStrict(order.fee_usd)],
              ["Confirmed with", order.confirmed_with === "face_id" ? "Face ID" : "Swipe"],
              ...(order.cancel_reason ? [["Cancelled", order.cancel_reason]] : []),
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3">
                <span className="text-muted-foreground">{k}</span><span className="numeric">{v}</span>
              </div>
            ))}
            {!!order.fills.length && (
              <div className="pt-2">
                <div className="text-muted-foreground mb-1">Fills</div>
                {order.fills.map((f) => (
                  <div key={`${f.timestamp}-${f.qty}`} className="flex justify-between numeric">
                    <span>{num(f.qty)} @ {num(f.price, 4)}</span>
                    <span className="text-muted-foreground">{timeAgo(f.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <EventStream events={events || []} />
        </div>
      )}
    </div>
  );
};
